import { useMemo } from 'react';

import { useActionStepApplyEntriesHook } from './action-step-apply-store';

export type ActionPlanApplyStepState = {
	inProgress: boolean;
	error: Error | undefined;
};

export type UseActionPlanApplyState = {
	perStep: Record<string, ActionPlanApplyStepState>;
	/**
	 * Runs `fn`, tracking its lifecycle in the shared store for only the given `invocationIds`
	 * (which may be a subset of the full list the hook was constructed with): sets `inProgress`
	 * true for each, awaits `fn`, clears `error` on success or sets it (wrapped in an `Error` if
	 * needed) on throw for each, then sets `inProgress` false for each.
	 */
	runApply: (invocationIds: string[], fn: () => Promise<void>) => Promise<void>;
};

/**
 * Shared, store-backed apply state for a whole plan (list of steps), keyed by `conversationId` +
 * `invocationIds`. Reads the same store as `useActionStepApplyState` for all given ids in one
 * selector call, rather than calling the single-step hook in a loop, which would break the rules
 * of hooks. Pass a memoised `invocationIds` array so the result stays referentially stable across
 * renders.
 */
export function useActionPlanApplyState(
	conversationId: string,
	invocationIds: string[],
): UseActionPlanApplyState {
	const [perStep, actions] = useActionStepApplyEntriesHook({ conversationId, invocationIds });

	const runApply = useMemo(
		() =>
			async (ids: string[], fn: () => Promise<void>): Promise<void> => {
				for (const id of ids) {
					actions.setInProgress(conversationId, id, true);
				}
				try {
					await fn();
					for (const id of ids) {
						actions.setError(conversationId, id, undefined);
					}
				} catch (e: unknown) {
					const error = e instanceof Error ? e : new Error(String(e));
					for (const id of ids) {
						actions.setError(conversationId, id, error);
					}
				} finally {
					for (const id of ids) {
						actions.setInProgress(conversationId, id, false);
					}
				}
			},
		[actions, conversationId],
	);

	return { perStep, runApply };
}
